/**
 * Date and time display in the object time zone (TZ 0.1, 8.13: "Barcha vaqtlar obyekt vaqt zonasida"), 24-hour
 * clock (8.12.3). Storage and API times are UTC; conversion happens only here.
 */
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

export const DEFAULT_TIME_ZONE = 'Asia/Tashkent';

type DateInput = Date | string | number;

function toDate(dateInput: DateInput): Date {
  return dateInput instanceof Date ? dateInput : new Date(dateInput);
}

/** "14:07" */
export function formatTime24(dateInput: DateInput, timeZone: string = DEFAULT_TIME_ZONE): string {
  return formatInTimeZone(toDate(dateInput), timeZone, 'HH:mm');
}

/** "14:07:54" */
export function formatTimeWithSeconds(dateInput: DateInput, timeZone: string = DEFAULT_TIME_ZONE): string {
  return formatInTimeZone(toDate(dateInput), timeZone, 'HH:mm:ss');
}

/** "30.09.2026" (uz/ru convention) or "2026-09-30" for en/ISO. */
export function formatDisplayDate(
  dateInput: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  pattern: 'dotted' | 'iso' = 'dotted',
): string {
  return formatInTimeZone(toDate(dateInput), timeZone, pattern === 'iso' ? 'yyyy-MM-dd' : 'dd.MM.yyyy');
}

/** "30.09.2026 14:07" */
export function formatDateTime(dateInput: DateInput, timeZone: string = DEFAULT_TIME_ZONE): string {
  return formatInTimeZone(toDate(dateInput), timeZone, 'dd.MM.yyyy HH:mm');
}

/**
 * A trading-day string "YYYY-MM-DD" is a calendar label, not an instant; parsing it as UTC would shift the day
 * in western zones. This returns a local Date at midnight of that label for calendar arithmetic.
 */
export function parseTradingDate(tradingDate: string): Date {
  const [yearText = '1970', monthText = '1', dayText = '1'] = tradingDate.split('-');
  return new Date(Number(yearText), Number(monthText) - 1, Number(dayText));
}

/** Local calendar Date -> "YYYY-MM-DD" trading-day label. */
export function toTradingDateString(calendarDate: Date): string {
  const yearText = String(calendarDate.getFullYear()).padStart(4, '0');
  const monthText = String(calendarDate.getMonth() + 1).padStart(2, '0');
  const dayText = String(calendarDate.getDate()).padStart(2, '0');
  return `${yearText}-${monthText}-${dayText}`;
}

/** "Today" in the object time zone as a trading-day label (calendar date; cutoff is applied by the backend). */
export function todayInTimeZone(now: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
  return formatInTimeZone(now, timeZone, 'yyyy-MM-dd');
}

/** The current calendar month "YYYY-MM" in the object time zone (month pickers, monthly targets). */
export function monthInTimeZone(now: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
  return formatInTimeZone(now, timeZone, 'yyyy-MM');
}

/**
 * The instant (ISO, UTC) at which the calendar day `tradingDate` ("YYYY-MM-DD") begins in the object time zone;
 * `dayOffset` moves to a later/earlier day first (1 = the start of the next day, the end of a half-open range).
 * The browser's own time zone plays no part.
 */
export function dayStartInstantInTimeZone(
  tradingDate: string,
  timeZone: string = DEFAULT_TIME_ZONE,
  dayOffset = 0,
): string {
  const [yearText = '1970', monthText = '1', dayText = '1'] = tradingDate.split('-');
  const shiftedLabel = formatInTimeZone(
    new Date(Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText) + dayOffset)),
    'UTC',
    'yyyy-MM-dd',
  );
  return fromZonedTime(`${shiftedLabel}T00:00:00`, timeZone).toISOString();
}

/** Hour of day 0..23 of an instant in the object time zone. */
export function hourInTimeZone(dateInput: DateInput, timeZone: string = DEFAULT_TIME_ZONE): number {
  return toZonedTime(toDate(dateInput), timeZone).getHours();
}

/** Wall-clock time in the object zone -> UTC instant (used by mocks and schedule editors). */
export function zonedWallClockToUtc(wallClock: Date | string, timeZone: string = DEFAULT_TIME_ZONE): Date {
  return fromZonedTime(wallClock, timeZone);
}

/** "08" -> "08:00" style hour label for hourly axes. */
export function formatHourLabel(hourOfDay: number): string {
  return `${String(hourOfDay).padStart(2, '0')}:00`;
}

/** Minutes -> "10:24" (hours:minutes); used for worked hours and deviations. */
export function formatMinutesAsClock(totalMinutes: number | null | undefined): string {
  if (totalMinutes === null || totalMinutes === undefined || !Number.isFinite(totalMinutes)) {
    return '—';
  }
  const roundedMinutes = Math.round(Math.abs(totalMinutes));
  const hoursPart = Math.floor(roundedMinutes / 60);
  const minutesPart = roundedMinutes % 60;
  const signText = totalMinutes < 0 ? '−' : '';
  return `${signText}${hoursPart}:${String(minutesPart).padStart(2, '0')}`;
}
